import { normalizeDayValues } from "../../utils/openingHours";

export const DAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export const DAY_SHORT_LABELS = {
  monday: "Mo",
  tuesday: "Di",
  wednesday: "Mi",
  thursday: "Do",
  friday: "Fr",
  saturday: "Sa",
  sunday: "So",
};

export const SLOT_STATUS_META = {
  open: {
    label: "Offen",
    chipColor: "success",
    icon: "mdi:calendar-check-outline",
    bg: "rgba(34, 197, 94, 0.08)",
    border: "rgba(22, 163, 74, 0.30)",
    text: "#16a34a",
  },
  mine: {
    label: "Mein Slot",
    chipColor: "primary",
    icon: "mdi:account-check-outline",
    bg: "rgba(14, 116, 144, 0.08)",
    border: "rgba(14, 116, 144, 0.30)",
    text: "#0e7490",
  },
  occupied: {
    label: "Besetzt",
    chipColor: "warning",
    icon: "mdi:account-lock-outline",
    bg: "rgba(245, 158, 11, 0.08)",
    border: "rgba(217, 119, 6, 0.30)",
    text: "#d97706",
  },
};

export const SITE_ACCENTS = [
  { bg: "rgba(14, 116, 144, 0.10)", border: "rgba(14, 116, 144, 0.32)", text: "#0e7490" },
  { bg: "rgba(22, 163, 74, 0.10)", border: "rgba(22, 163, 74, 0.32)", text: "#15803d" },
  { bg: "rgba(245, 158, 11, 0.10)", border: "rgba(217, 119, 6, 0.32)", text: "#b45309" },
  { bg: "rgba(225, 29, 72, 0.10)", border: "rgba(225, 29, 72, 0.32)", text: "#be123c" },
  { bg: "rgba(37, 99, 235, 0.10)", border: "rgba(37, 99, 235, 0.32)", text: "#1d4ed8" },
  { bg: "rgba(71, 85, 105, 0.10)", border: "rgba(71, 85, 105, 0.32)", text: "#334155" },
];

export const getSiteAccent = (index) => SITE_ACCENTS[index % SITE_ACCENTS.length];

export const buildSessionKey = (session) =>
  [session.day, session.start, session.end, session.title || ""]
    .map((v) => encodeURIComponent(v || ""))
    .join("|");

export const normalizeAssignments = (assignments) => ({
  regular: Array.isArray(assignments?.regular) ? assignments.regular : [],
  single: Array.isArray(assignments?.single) ? assignments.single : [],
});

export const normalizeSiteSessions = (sessions = []) => {
  const safe = Array.isArray(sessions) ? sessions : [];
  return safe
    .flatMap((entry) => {
      const days = normalizeDayValues(entry?.days ?? entry?.day);
      return days.map((day) => ({
        day,
        title: entry?.title || "",
        start: entry?.start || "",
        end: entry?.end || "",
      }));
    })
    .sort((a, b) => {
      const dayDiff = DAY_ORDER.indexOf(a.day) - DAY_ORDER.indexOf(b.day);
      if (dayDiff !== 0) return dayDiff;
      return String(a.start).localeCompare(String(b.start));
    });
};

export const parseDateValue = (v) => {
  if (!v) return null;
  const d = new Date(`${v}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const toDateValue = (d) => {
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

export const compareDateValues = (a, b) =>
  String(a || "").localeCompare(String(b || ""));

export const addDays = (date, amount) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};

export const addMonths = (date, amount) => {
  const next = new Date(date);
  next.setMonth(next.getMonth() + amount);
  return next;
};

export const startOfWeek = (date) => {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12);
  const dayIndex = (next.getDay() + 6) % 7;
  next.setDate(next.getDate() - dayIndex);
  return next;
};

export const getWeekDateValues = (anchor) => {
  const start = startOfWeek(anchor);
  return Array.from({ length: 7 }, (_, i) => toDateValue(addDays(start, i)));
};

export const getMonthDateValues = (anchor) => {
  const y = anchor.getFullYear();
  const m = anchor.getMonth();
  const days = new Date(y, m + 1, 0).getDate();
  return Array.from({ length: days }, (_, i) => toDateValue(new Date(y, m, i + 1, 12)));
};

export const getMonthCells = (anchor) => {
  const y = anchor.getFullYear();
  const m = anchor.getMonth();
  const firstDay = new Date(y, m, 1, 12);
  const offset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];

  for (let i = 0; i < offset; i += 1) {
    cells.push({ key: `empty-${i}`, date: "", label: "", isCurrentMonth: false });
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    const d = new Date(y, m, day, 12);
    cells.push({ key: toDateValue(d), date: toDateValue(d), label: String(day), isCurrentMonth: true });
  }
  return cells;
};

export const getDayValueFromDate = (dateValue) => {
  const d = parseDateValue(dateValue);
  if (!d) return "";
  return DAY_ORDER[(d.getDay() + 6) % 7] || "";
};

export const formatDateLabel = (dateValue, options = {}) => {
  const d = parseDateValue(dateValue);
  if (!d) return "";
  return d.toLocaleDateString("de-DE", options);
};

export const isDateWithinRange = (dateValue, startDate, endDate) => {
  if (!dateValue) return false;
  if (startDate && compareDateValues(dateValue, startDate) < 0) return false;
  if (endDate && compareDateValues(dateValue, endDate) > 0) return false;
  return true;
};

export const isDateWithinAssignmentRange = (dateValue, assignment) =>
  isDateWithinRange(dateValue, assignment?.startDate, assignment?.endDate);

export const getRegularAssignmentForDate = (entries = [], dateValue) =>
  [...entries]
    .filter((entry) => isDateWithinAssignmentRange(dateValue, entry))
    .sort((a, b) => compareDateValues(b?.startDate, a?.startDate))[0] || null;

export const getEndOfMonthDateValue = (dateValue) => {
  const d = parseDateValue(dateValue);
  if (!d) return "";
  return toDateValue(new Date(d.getFullYear(), d.getMonth() + 1, 0, 12));
};

export const getRecurringMonthsEndDateValue = (dateValue, months) => {
  const d = parseDateValue(dateValue);
  if (!d) return "";
  const safeMonths = Math.max(1, Math.floor(Number(months) || 1));
  return toDateValue(new Date(d.getFullYear(), d.getMonth() + safeMonths, 0, 12));
};

export const getAssignmentRangeLabel = (assignment, options = {}) => {
  if (!assignment) return "";
  const { long = false } = options;
  const dateOptions = long
    ? { day: "2-digit", month: "2-digit", year: "numeric" }
    : { day: "2-digit", month: "2-digit" };
  const startLabel = assignment?.startDate
    ? formatDateLabel(assignment.startDate, dateOptions)
    : "";
  const endLabel = assignment?.endDate
    ? formatDateLabel(assignment.endDate, dateOptions)
    : "";

  if (startLabel && endLabel) return `${startLabel} – ${endLabel}`;
  if (startLabel && !endLabel) return `ab ${startLabel}`;
  if (!startLabel && endLabel) return `bis ${endLabel}`;
  return "Bis auf Weiteres";
};

export const formatUserLabel = (user) =>
  `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.mail || "Trainer";

export const getAssignmentLabel = (a) => a?.trainerName || a?.trainerLabel || "Trainer";

export const isSameTrainer = (a, b) => String(a || "") === String(b || "");

export const parseTimeToMinutes = (v) => {
  if (!v) return 0;
  const [h, m] = String(v).split(":");
  return (Number.isFinite(+h) ? +h : 0) * 60 + (Number.isFinite(+m) ? +m : 0);
};

export const formatMinutesToTime = (v) => {
  const safe = Math.max(0, Math.floor(v));
  return `${String(Math.floor(safe / 60)).padStart(2, "0")}:${String(safe % 60).padStart(2, "0")}`;
};

export const compareSlots = (a, b) => {
  const sd = parseTimeToMinutes(a?.session?.start) - parseTimeToMinutes(b?.session?.start);
  if (sd !== 0) return sd;
  const ed = parseTimeToMinutes(a?.session?.end) - parseTimeToMinutes(b?.session?.end);
  if (ed !== 0) return ed;
  const nd = String(a?.siteName || "").localeCompare(String(b?.siteName || ""), "de");
  if (nd !== 0) return nd;
  return String(a?.session?.title || "").localeCompare(String(b?.session?.title || ""), "de");
};

export const groupOverlappingSlots = (slots = []) => {
  const sorted = [...slots].sort(compareSlots);
  const groups = [];

  sorted.forEach((slot) => {
    const startM = parseTimeToMinutes(slot?.session?.start);
    const endM = Math.max(startM + 1, parseTimeToMinutes(slot?.session?.end));
    const cur = groups[groups.length - 1];

    if (cur && startM < cur.endMinutes) {
      cur.endMinutes = Math.max(cur.endMinutes, endM);
      cur.slots.push(slot);
      return;
    }

    groups.push({
      key: `${slot?.date || "slot"}-${groups.length}`,
      startMinutes: startM,
      endMinutes: endM,
      slots: [slot],
    });
  });

  return groups.map((g) => ({
    ...g,
    startLabel: formatMinutesToTime(g.startMinutes),
    endLabel: formatMinutesToTime(g.endMinutes),
    overlapping: g.slots.length > 1,
  }));
};

/** Count how many times a given weekday (DAY_ORDER value) occurs in a specific month */
export const countWeekdayInMonth = (year, month, dayName) => {
  const dayIndex = DAY_ORDER.indexOf(dayName);
  if (dayIndex < 0) return 0;
  // JS getDay(): 0=Sun … 6=Sat  →  convert DAY_ORDER index (0=Mon) to JS day
  const jsDow = (dayIndex + 1) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow = new Date(year, month, 1).getDay();
  // offset = days until first occurrence of jsDow
  const offset = (jsDow - firstDow + 7) % 7;
  return Math.floor((daysInMonth - offset - 1) / 7) + 1;
};

export const getRangeLabel = (view, anchor) => {
  if (view === "month") {
    return anchor.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  }
  const dates = getWeekDateValues(anchor);
  const s = formatDateLabel(dates[0], { day: "2-digit", month: "2-digit" });
  const e = formatDateLabel(dates[6], { day: "2-digit", month: "2-digit", year: "numeric" });
  return `${s} – ${e}`;
};
